export enum ApiKeyStatus {
  Active = 1,
  Expired = 2,
  Revoked = 3
}

export interface ApiKey {
  id: string
  plan: string
  expiresAt: string
  status: ApiKeyStatus
  usage?: UsageCounters
}

export interface CreateApiKey {
  id: string
  plan: string
  expiresAt?: string
  status: ApiKeyStatus
}

export interface UpdateApiKey {
  plan: string
  expiresAt: string
  status: ApiKeyStatus
}

export interface Plan {
  name: string
  minuteLimit: number
  dayLimit: number
  monthLimit: number
}

export interface CreateOrUpdatePlan {
  name: string
  minuteLimit: number
  dayLimit: number
  monthLimit: number
}

export interface UsageCounters {
  minute: number
  day: number
  month: number
  lastUpdated: string
  minuteExpiresAt?: string
  dayExpiresAt?: string
  monthExpiresAt?: string
}

export interface DashboardStats {
  totalApiKeys: number
  totalPlans: number
  redisConnected: boolean
  redisPingMs: number
  lastUpdated: string
  // Extended stats
  storageConnected?: boolean
  synchronizerStatus?: string
  lastSnapshotTime?: string
  lastRestoreTime?: string
}

export interface FunctionTestRequest {
  apiKey: string
  endpoint: string
  method: string
  path: string
  requestBody: string
  headers: Record<string, string>
  queryParameters: Record<string, string>
}

export interface FunctionTestResponse {
  success: boolean
  statusCode: number
  statusText: string
  responseHeaders: Record<string, string>
  responseBody: string
  elapsedMs: number
  errorMessage?: string
  timestamp: string
}

export interface QuickTest {
  apiKey: string
  requestBody?: string
}

export interface ProxyTest {
  apiKey: string
  method?: string
  path?: string
  requestBody?: string
  queryParameters?: Record<string, string>
}

export interface BKSearchTest {
  query: string
  language: string
}

export interface TestConfiguration {
  gatekeeperFunctionUrl: string
  bkSearchFunctionUrl: string
  environment: string
}

// Synchronizer types
export interface SynchronizerStatus {
  isRunning: boolean
  lastSnapshotTime?: string
  lastRestoreTime?: string
  lastSnapshotStatus?: string
  lastRestoreStatus?: string
  lastSnapshotDurationMs?: number
  lastRestoreDurationMs?: number
  lastSnapshotKeysProcessed?: number
  lastSnapshotKeysStored?: number
  lastRestorePlansRestored?: number
  lastRestoreApiKeysRestored?: number
  lastRestoreSnapshotsRestored?: number
  lastError?: string
  lastErrorTime?: string
}

export interface SynchronizerHistory {
  operationType: string
  timestamp: string
  isSuccess: boolean
  durationMs: number
  correlationId: string
  errorMessage?: string
  keysProcessed?: number
  snapshotsStored?: number
  plansRestored?: number
  apiKeysRestored?: number
  ratelimitSnapshotsRestored?: number
  wasColdStart?: boolean
}

export interface TriggerSynchronizer {
  operationType: string
  forceRestore?: boolean
  batchSize?: number
  scanCount?: number
}

export interface SynchronizerOperationResult {
  isSuccess: boolean
  errorMessage?: string
  durationMs: number
  timestamp: string
  correlationId: string
  operationType: string
  keysProcessed?: number
  snapshotsStored?: number
  plansRestored?: number
  apiKeysRestored?: number
  ratelimitSnapshotsRestored?: number
  wasColdStart?: boolean
}

// Azure Storage types
export interface TableStorageApiKey {
  id: string
  plan: string
  status: ApiKeyStatus
  expiresAt?: string
  subscriptionId?: string // Subscription/Customer ID for shop integration
  timestamp?: string
  eTag?: string
}

export interface TableStoragePlan {
  name: string
  minuteLimit: number
  dayLimit: number
  monthLimit: number
  timestamp?: string
  eTag?: string
}

export interface RatelimitSnapshot {
  partitionKey: string
  rowKey: string
  apiKeyId: string
  window: string
  value: number
  ttlMs: number
  snapshotUtc: string
  timestamp?: string
  eTag?: string
  isValid: boolean
  expiresAt: string
}

export interface CreateTableStorageApiKey {
  id: string
  plan: string
  status: ApiKeyStatus
  expiresAt?: string
  shopId?: string // User/Customer ID for shop integration
}

export interface CreateTableStoragePlan {
  name: string
  minuteLimit: number
  dayLimit: number
  monthLimit: number
}

export interface AzureStorageStats {
  totalApiKeysInStorage: number
  activeApiKeysInStorage: number
  expiredApiKeysInStorage: number
  suspendedApiKeysInStorage: number
  totalPlansInStorage: number
  totalSnapshotsToday: number
  validSnapshotsToday: number
  expiredSnapshotsToday: number
  storageConnected: boolean
  lastUpdated: string
}

export interface SnapshotQuery {
  apiKeyId?: string
  window?: string
  fromDate?: string
  toDate?: string
  onlyValid?: boolean
  take?: number
}
